/**
 * PlanPhotoPicker — modal for adding one or more photo references to a Daily Plan note.
 *
 * Three sources:
 *  1. Upload from device → Cloudinary via `/api/doserne/daily-plans/upload-image` (multi supported)
 *  2. Paste an image URL manually (single)
 *  3. Pick from recent Daily Progress photos across all projects (multi-select)
 *
 * Returns an ARRAY of {url, label} via `onPick`. Each picked photo gets
 * an auto-incrementing label "Poza N".
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Link2, Image as ImageIcon, Loader2, Check, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition ${
        active ? "border-emerald-500 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"
      }`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

export default function PlanPhotoPicker({ open, onClose, onPick, authHeader, nextNumber = 1 }) {
  const [tab, setTab] = useState("upload"); // upload | url | gallery
  const [busy, setBusy] = useState(false);

  // Upload state — array of {url, filename}
  const [uploaded, setUploaded] = useState([]);
  // URL state (single)
  const [urlInput, setUrlInput] = useState("");
  const [urlLabel, setUrlLabel] = useState(`Poza ${nextNumber}`);
  // Gallery state
  const [gallery, setGallery] = useState(null);
  const [galleryQ, setGalleryQ] = useState("");
  const [selectedUrls, setSelectedUrls] = useState([]); // ordered array of URLs

  useEffect(() => {
    if (open) {
      setUploaded([]);
      setUrlInput("");
      setUrlLabel(`Poza ${nextNumber}`);
      setSelectedUrls([]);
      setTab("upload");
    }
  }, [open, nextNumber]);

  // Lazy-load progress photos when gallery tab first opens.
  useEffect(() => {
    if (tab !== "gallery" || gallery !== null) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/progress?limit=100`, authHeader());
        const flat = [];
        (data.posts || []).forEach((p) => {
          (p.photos || []).forEach((ph) => {
            flat.push({
              url: ph.url,
              project: p.project_name || "",
              date: p.progress_date || "",
              note: p.note || "",
            });
          });
        });
        setGallery(flat);
      } catch {
        setGallery([]);
        toast.error("Kunne ikke laste bilder");
      }
    })();
  }, [tab, gallery, authHeader]);

  if (!open) return null;

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    const results = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/doserne/daily-plans/upload-image`, fd, {
          ...authHeader(),
          headers: { ...(authHeader().headers || {}), "Content-Type": "multipart/form-data" },
        });
        results.push({ url: data.url, filename: file.name });
      } catch {
        toast.error(`Opplasting feilet: ${file.name}`);
      }
    }
    if (results.length) {
      setUploaded((cur) => [...cur, ...results]);
      toast.success(`${results.length} bilde${results.length === 1 ? "" : "r"} lastet opp`);
    }
    setBusy(false);
  };

  const toggleGallerySelect = (url) => {
    setSelectedUrls((cur) => cur.includes(url) ? cur.filter((u) => u !== url) : [...cur, url]);
  };

  const removeUploaded = (idx) => {
    setUploaded((cur) => cur.filter((_, i) => i !== idx));
  };

  const confirmPick = () => {
    let picks = [];
    if (tab === "upload") {
      picks = uploaded.map((u, i) => ({ url: u.url, label: `Poza ${nextNumber + i}` }));
    } else if (tab === "url") {
      if (!urlInput.trim()) { toast.error("Introdu un URL"); return; }
      picks = [{ url: urlInput.trim(), label: urlLabel.trim() || `Poza ${nextNumber}` }];
    } else {
      picks = selectedUrls.map((url, i) => ({ url, label: `Poza ${nextNumber + i}` }));
    }
    if (!picks.length) { toast.error("Alege sau încarcă cel puțin o poză"); return; }
    onPick(picks);
  };

  const filteredGallery = gallery?.filter((g) => {
    if (!galleryQ.trim()) return true;
    const q = galleryQ.toLowerCase();
    return (g.project || "").toLowerCase().includes(q) || (g.note || "").toLowerCase().includes(q);
  }) || [];

  const totalPicked =
    tab === "upload" ? uploaded.length :
    tab === "url" ? (urlInput.trim() ? 1 : 0) :
    selectedUrls.length;

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-picker-backdrop"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="photo-picker-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Adaugă poze</div>
            <h3 className="text-lg font-black text-slate-900">Poți alege mai multe deodată</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="photo-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          <TabButton active={tab === "upload"} onClick={() => setTab("upload")} icon={Upload} testid="tab-upload">Încarcă</TabButton>
          <TabButton active={tab === "url"} onClick={() => setTab("url")} icon={Link2} testid="tab-url">URL</TabButton>
          <TabButton active={tab === "gallery"} onClick={() => setTab("gallery")} icon={ImageIcon} testid="tab-gallery">Din progres</TabButton>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {tab === "upload" && (
            <div className="space-y-3">
              <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition" data-testid="upload-drop-zone">
                <input
                  type="file" accept="image/*" multiple className="hidden"
                  onChange={(e) => doUpload(e.target.files)}
                  data-testid="upload-file-input"
                />
                {busy ? (
                  <><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400 mb-2" /><div className="text-sm text-slate-500">Se încarcă...</div></>
                ) : (
                  <><Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" /><div className="text-sm font-semibold text-slate-700">Apasă pentru a alege poze</div><div className="text-xs text-slate-500 mt-1">JPG, PNG, WebP · max 15 MB fiecare · selecție multiplă</div></>
                )}
              </label>

              {uploaded.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1.5">
                    {uploaded.length} poz{uploaded.length === 1 ? "ă" : "e"} încărcat{uploaded.length === 1 ? "ă" : "e"}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {uploaded.map((u, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden border-2 border-emerald-500 ring-2 ring-emerald-300">
                        <img src={u.url} alt={u.filename} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeUploaded(i)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-900/80 flex items-center justify-center text-white"
                          data-testid={`upload-remove-${i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                        <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1 py-0.5 font-semibold">
                          Poza {nextNumber + i}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "url" && (
            <div className="space-y-3">
              <label className="block">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Lipește URL imagine</div>
                <input
                  type="url" value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="url-input"
                />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Etichetă</div>
                <input
                  value={urlLabel}
                  onChange={(e) => setUrlLabel(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  placeholder={`Poza ${nextNumber}`}
                  data-testid="url-label-input"
                />
              </label>
              {urlInput.trim() && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <img
                    src={urlInput} alt=""
                    className="w-full max-h-40 object-contain bg-slate-50"
                    onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                  />
                </div>
              )}
            </div>
          )}

          {tab === "gallery" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    value={galleryQ}
                    onChange={(e) => setGalleryQ(e.target.value)}
                    placeholder="Caută după proiect sau notă..."
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    data-testid="gallery-search"
                  />
                </div>
                {selectedUrls.length > 0 && (
                  <span className="text-[10px] uppercase tracking-widest font-black text-emerald-700 bg-emerald-100 px-2 py-1.5 rounded">
                    {selectedUrls.length} selectate
                  </span>
                )}
              </div>
              {gallery === null ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : filteredGallery.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-500">Niciun rezultat.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {filteredGallery.map((g, i) => {
                    const idx = selectedUrls.indexOf(g.url);
                    const active = idx >= 0;
                    return (
                      <button
                        type="button" key={i}
                        onClick={() => toggleGallerySelect(g.url)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition ${active ? "border-emerald-500 ring-2 ring-emerald-300" : "border-slate-200 hover:border-slate-400"}`}
                        data-testid={`gallery-item-${i}`}
                        title={`${g.project} · ${g.date}`}
                      >
                        <img src={g.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        {active && (
                          <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[10px] font-black">
                            {idx + 1}
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                          <div className="text-[9px] text-white font-semibold truncate">{g.project || "—"}</div>
                          <div className="text-[8px] text-white/70">{g.date}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 rounded-lg"
            data-testid="photo-picker-cancel"
          >
            Avbryt
          </button>
          <button
            onClick={confirmPick}
            disabled={busy || totalPicked === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50"
            data-testid="photo-picker-confirm"
          >
            <Check className="w-4 h-4" /> Adaugă{totalPicked > 1 ? ` ${totalPicked}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
